/**
 * Progress Summary — compact completion rollup for a Plan of Record, computed
 * client-side from the assembled object (no extra fetches).
 *
 *  - "X of N commitments complete" + a thin complete/total bar.
 *  - Per-status counts (complete / active / next / blocked / watch).
 *  - Day counter: days since the record first entered `approved` status (the
 *    earliest `status_change` governance event whose new_value.status is
 *    "approved" — appended by the set_por_status RPC). If the record has never
 *    been approved, falls back to days since creation with that honest label.
 */

import type {
  AssembledPor,
  PorCommitmentStatus,
  PorGovernanceEvent,
} from '../../types/plan-of-record';
import { CommitmentStatusPill, PorCard, formatDate } from './porUi';

const STATUS_ORDER: PorCommitmentStatus[] = ['complete', 'active', 'next', 'blocked', 'watch'];

/** Earliest governance event that moved the record into `approved`, if any. */
function firstApprovalEvent(events: PorGovernanceEvent[]): PorGovernanceEvent | null {
  // assemble_por returns events newest-first; scan from the oldest end.
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (ev.event_type !== 'status_change') continue;
    const next = ev.new_value as { status?: unknown } | null;
    if (next && next.status === 'approved') return ev;
  }
  return null;
}

function dayNumberSince(iso: string): number | null {
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return null;
  // The start date itself is Day 1.
  return Math.max(1, Math.floor((Date.now() - start) / 86_400_000) + 1);
}

export function PorProgressSummary({ assembled }: { assembled: AssembledPor }) {
  const { record, commitments, governance_events } = assembled;

  const total = commitments.length;
  const counts = commitments.reduce<Record<PorCommitmentStatus, number>>(
    (acc, c) => {
      acc[c.status] += 1;
      return acc;
    },
    { active: 0, next: 0, blocked: 0, complete: 0, watch: 0 },
  );
  const complete = counts.complete;
  const pct = total > 0 ? Math.round((complete / total) * 100) : 0;

  const approval = firstApprovalEvent(governance_events);
  const anchorIso = approval ? approval.created_at : record.created_at;
  const dayN = dayNumberSince(anchorIso);

  return (
    <PorCard>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Progress
          </div>
          {total === 0 ? (
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1.5">
              No commitments on record yet — there is nothing to measure completion against.
            </p>
          ) : (
            <>
              <p className="text-lg font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-1 tabular-nums">
                {complete} of {total} commitment{total === 1 ? '' : 's'} complete
              </p>
              <div
                className="mt-2 h-1.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--hover-tint)] overflow-hidden"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Commitments complete"
              >
                <div className="h-full bg-primary-600" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap mt-3">
                {STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5">
                    <span className="text-xs font-mono tabular-nums text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                      {counts[s]}
                    </span>
                    <CommitmentStatusPill status={s} />
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Day counter — honest about its anchor */}
        {dayN != null && (
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-mono font-semibold tabular-nums text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Day {dayN}
            </div>
            <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              {approval
                ? `since approved ${formatDate(anchorIso)}`
                : `since created ${formatDate(anchorIso)} — not yet approved`}
            </div>
          </div>
        )}
      </div>
    </PorCard>
  );
}
